import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Clock, ArrowRight } from "lucide-react";
import { buildWhatsAppUrl } from "../../lib/constants";
import { trackEvent } from "../../lib/api";

// Slides usando archivos REALES del banco del cliente
const HERO_SLIDES = [
  {
    image: "/images/proyecto_torre_benavides_terraza_01.jpeg",
    label: "Torre Benavides",
    location: "Monterrey, NL",
    sqm: "340 m²",
    material: "Membrana PVC",
  },
  {
    image: "/images/proyecto_hero_estacionamiento.jpeg",
    label: "Automotriz Rivero Seminuevos",
    location: "Monterrey, NL",
    sqm: "880 m²",
    material: "Velaria comercial",
  },
  {
    image: "/images/proyecto_gradas_deportivas_avispones.jpg",
    label: "Gradas Avispones",
    location: "Monterrey, NL",
    sqm: "620 m²",
    material: "Cubierta deportiva",
  },
  {
    image: "/images/galeria_tensoestructura_03.jpg",
    label: "Cancha de pádel techada",
    location: "Proyecto deportivo · CDMX",
    sqm: "1,100 m²",
    material: "Tensoestructura arquitectónica",
  },
];

// Logos del Hero (mini wall) — los más reconocibles del banco real
const HERO_LOGOS = [
  { src: "/images/clientes/cliente_2-1.png", alt: "Liverpool" },
  { src: "/images/clientes/cliente_17-2.png", alt: "OXXO" },
  { src: "/images/clientes/cliente_10-2.png", alt: "FEMSA" },
  { src: "/images/clientes/cliente_13-2.png", alt: "UANL" },
  { src: "/images/clientes/cliente_11-2.png", alt: "Walmart" },
  { src: "/images/clientes/cliente_12-2.png", alt: "Soriana" },
];

const SLIDE_DURATION = 6000;

export default function Hero() {
  const [activeIdx, setActiveIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => {
      setActiveIdx((i) => (i + 1) % HERO_SLIDES.length);
    }, SLIDE_DURATION);
    return () => clearInterval(t);
  }, [paused]);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const slide = HERO_SLIDES[activeIdx];

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative w-full bg-[#FAFAF7] overflow-hidden"
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 lg:min-h-screen lg:max-h-[1080px]" style={{ minHeight: "640px" }}>
        {/* LEFT — Editorial content */}
        <div className="lg:col-span-7 flex flex-col justify-center px-6 sm:px-10 lg:pl-24 lg:pr-12 pt-32 lg:pt-32 pb-12 lg:pb-20 order-1 lg:order-1">
          <div className="max-w-2xl">
            {/* Eyebrow */}
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: "easeOut" }}
              className="font-sans font-medium text-[#3D5311] text-[12px] uppercase tracking-[0.18em] flex flex-wrap items-center gap-x-2 gap-y-1"
              data-testid="hero-eyebrow"
            >
              <span>+100 PROYECTOS</span>
              <span className="text-[#8cb62c]">·</span>
              <span>IFAI 2021</span>
              <span className="text-[#8cb62c]">·</span>
              <span>20 AÑOS</span>
              <span className="text-[#8cb62c]">·</span>
              <span>MONTERREY</span>
            </motion.p>

            {/* H1 editorial */}
            <h1
              className="mt-7 font-display font-black tracking-tightest text-[#1F2937] leading-[0.95] text-[44px] sm:text-[56px] md:text-[72px] lg:text-[88px]"
              data-testid="hero-h1"
            >
              <motion.span
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: "easeOut", delay: 0.08 }}
                className="block"
              >
                Velarias y
              </motion.span>
              <motion.span
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: "easeOut", delay: 0.16 }}
                className="block font-editorial italic font-bold text-[#8cb62c] leading-[1] tracking-tight"
              >
                Tensoestructuras
              </motion.span>
              <motion.span
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: "easeOut", delay: 0.24 }}
                className="block"
              >
                a la medida
              </motion.span>
            </h1>

            {/* Subtitle */}
            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: "easeOut", delay: 0.28 }}
              className="mt-7 max-w-[540px] text-[#6B7280] font-sans text-[17px] md:text-[19px] leading-[1.55]"
              data-testid="hero-subtitle"
            >
              Diseñamos, fabricamos e instalamos arquitectura textil premium para corporativos, espacios deportivos y residencial. Cobertura nacional desde Monterrey.
            </motion.p>

            {/* CTAs */}
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: "easeOut", delay: 0.36 }}
              className="mt-10 flex flex-col sm:flex-row gap-4"
            >
              <button
                type="button"
                onClick={() => {
                  trackEvent("cta_hero_quote_click", { location: "hero_primary" });
                  scrollTo("cotiza");
                }}
                className="group inline-flex items-center justify-center gap-2 px-8 py-[18px] bg-[#8cb62c] text-white font-display font-bold uppercase tracking-wide text-sm transition-all duration-200 hover:bg-[#7BA322] hover:scale-[1.02]"
                style={{ boxShadow: "0 8px 24px rgba(140,182,44,0.25)" }}
                data-testid="hero-cta-quote"
              >
                Cotiza tu proyecto gratis
                <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
              </button>
              <button
                type="button"
                onClick={() => scrollTo("proyectos")}
                className="inline-flex items-center justify-center gap-2 px-8 py-[18px] border border-[#1F2937] text-[#1F2937] font-display font-bold uppercase tracking-wide text-sm transition-colors duration-200 hover:bg-[#F2F4ED]"
                data-testid="hero-cta-gallery"
              >
                Ver proyectos realizados
              </button>
            </motion.div>

            {/* Trust signal */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: "easeOut", delay: 0.44 }}
              className="mt-8 inline-flex items-center gap-2 text-[#6B7280] font-sans font-medium text-[14px]"
              data-testid="hero-trust-signal"
            >
              <Clock className="w-4 h-4 text-[#8cb62c]" />
              Cotización formal en menos de 24 horas hábiles.
            </motion.div>

            {/* Catalog secondary link */}
            <button
              onClick={() => {
                const el = document.querySelector("[data-testid=catalog-section]");
                if (el) el.scrollIntoView({ behavior: "smooth" });
              }}
              className="mt-6 inline-flex items-center gap-2 text-[#6B7280] hover:text-[#3D5311] font-mono text-[11px] uppercase tracking-[0.22em] transition-colors"
              data-testid="hero-secondary-catalog"
            >
              o descarga el catálogo gratis →
            </button>
          </div>
        </div>

        {/* RIGHT — Image carousel + floating card */}
        <div
          className="relative lg:col-span-5 order-2 lg:order-2 h-[60vh] lg:h-auto"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          data-testid="hero-carousel"
        >
          {/* Slides */}
          <div className="absolute inset-0 overflow-hidden" style={{ boxShadow: "-16px 24px 60px rgba(31,41,55,0.18)" }}>
            <AnimatePresence mode="sync">
              <motion.img
                key={activeIdx}
                src={slide.image}
                alt={slide.label}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 1.0, ease: "easeOut" }}
                className="absolute inset-0 w-full h-full object-cover"
                loading={activeIdx === 0 ? "eager" : "lazy"}
                fetchpriority={activeIdx === 0 ? "high" : "auto"}
              />
            </AnimatePresence>
          </div>

          {/* Slide indicators (top-right, minimal) */}
          <div className="absolute top-6 right-6 flex gap-2 z-10" data-testid="hero-slide-dots">
            {HERO_SLIDES.map((_, i) => (
              <button
                key={i}
                onClick={() => setActiveIdx(i)}
                aria-label={`Slide ${i + 1}`}
                className={`h-[3px] transition-all duration-300 ${
                  i === activeIdx ? "w-10 bg-white" : "w-5 bg-white/50 hover:bg-white/80"
                }`}
                data-testid={`hero-dot-${i}`}
              />
            ))}
          </div>

          {/* Floating project card */}
          <div
            className="absolute z-20 left-6 right-6 bottom-6 lg:left-auto lg:right-auto lg:-left-12 lg:bottom-10 lg:w-[300px] bg-white border border-[#DDE2D5] px-6 py-5"
            style={{ boxShadow: "0 12px 32px rgba(31,41,55,0.12)", borderLeft: "4px solid #8cb62c" }}
            data-testid="hero-floating-card"
          >
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#3D5311] font-semibold">
              Proyecto destacado
            </p>
            <AnimatePresence mode="wait">
              <motion.div
                key={`info-${activeIdx}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <h3
                  className="mt-2 font-display font-extrabold text-[22px] text-[#1F2937] leading-tight"
                  data-testid="hero-card-label"
                >
                  {slide.label}
                </h3>
                <p className="mt-0.5 text-[#6B7280] font-sans text-[14px]" data-testid="hero-card-location">
                  {slide.location}
                </p>
                <span className="block mt-3 mb-3 h-[2px] w-6 bg-[#8cb62c]" aria-hidden="true" />
                <p className="text-[#1F2937] font-sans font-medium text-[13px]" data-testid="hero-card-meta">
                  {slide.sqm}
                  <span className="mx-2 text-[#8cb62c]">·</span>
                  {slide.material}
                </p>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Logo wall compacto — debajo del hero, antes de Statsbar */}
      <div className="bg-white border-t border-[#DDE2D5] overflow-hidden" data-testid="hero-logo-wall">
        {/* Mobile: marquee horizontal infinito */}
        <div className="sm:hidden py-5 overflow-hidden">
          <div className="flex animate-marquee items-center gap-12 whitespace-nowrap will-change-transform">
            {[...HERO_LOGOS, ...HERO_LOGOS].map((logo, i) => (
              <img
                key={`m-${i}`}
                src={logo.src}
                alt={logo.alt}
                className="h-6 w-auto flex-shrink-0"
                style={{ filter: "grayscale(1)", opacity: 0.55 }}
                loading="lazy"
              />
            ))}
          </div>
        </div>

        {/* Desktop ≥640px: estático con label */}
        <div className="hidden sm:block container-tight py-7">
          <div className="flex flex-col md:flex-row items-center gap-6 md:gap-10">
            <p className="font-sans font-medium text-[12px] uppercase text-[#6B7280] tracking-[0.05em] flex-shrink-0">
              Marcas que confían en nosotros
            </p>
            <span className="hidden md:block h-6 w-px bg-[#DDE2D5]" aria-hidden="true" />
            <div className="flex-1 flex items-center gap-x-12 lg:gap-x-14 overflow-x-auto no-scrollbar">
              {HERO_LOGOS.map((logo) => (
                <img
                  key={logo.alt}
                  src={logo.src}
                  alt={logo.alt}
                  className="h-7 w-auto flex-shrink-0 transition-all duration-300"
                  style={{ filter: "grayscale(1)", opacity: 0.55 }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.filter = "grayscale(0)";
                    e.currentTarget.style.opacity = "1";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.filter = "grayscale(1)";
                    e.currentTarget.style.opacity = "0.55";
                  }}
                  loading="lazy"
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
